"use client";

import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { trackFormSubmit } from "../../lib/trackConversion";

export default function DynamicForm({ inputs, formType }) {
  const { register: registerField, handleSubmit, reset, watch, formState } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    shouldFocusError: false,
  });
  const formRef = useRef(null);
  const fields = inputs.flatMap(box => box.inputs);
  const requiredNames = new Set(["Requested By Full Legal Name Property Owner", "Phone", "Address", "Full Municipal Address",
    "Intended Use Of Appraisal", "Intended Use of This Appraisal",
    "Intended use of this appraisal lawyer/accountant/other"]);
  const isRequired = name => requiredNames.has(name) || fields.find(field => field.name === name)?.required === true;
  const register = (name, options = {}) => {
    const field = fields.find(item => item.name === name);
    return {
      ...registerField(name, {
        ...options,
        required: isRequired(name) ? "Please complete this field." : options.required,
        validate: value => !isRequired(name) || String(value ?? "").trim().length > 0 || "Please complete this field.",
        ...(field?.type === "email" ? {
          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address." },
        } : {}),
      }),
      id: name,
      "aria-required": isRequired(name) || undefined,
      "aria-invalid": Boolean(formState.errors[name]),
      "aria-describedby": formState.errors[name] ? `${name}-error` : undefined,
    };
  };
  const fieldCaption = input => (
    <>
      {input.label}{isRequired(input.name) && <span className="text-red-700 dark:text-red-300"> *<span className="sr-only"> required</span></span>}:
      {formState.errors[input.name] && <span id={`${input.name}-error`} className="block text-sm text-red-700 dark:text-red-300">
        {formState.errors[input.name].message || "Please complete this field."}
      </span>}
    </>
  );
  const focusField = name => {
    const field = Array.from(formRef.current?.elements || []).find(element => element.name === name);
    if (field) {
      field.focus({ preventScroll: true });
      field.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const onSubmit = async (data) => {
    try {
      setErrorMessage("");

      const formData = new FormData();

      for (const box of inputs) {
        for (const input of box.inputs) {
          if (input.type === "file") {
            if (data[input.name]?.[0]) {
              formData.append(input.name, data[input.name][0]);
            }
          } else {
            formData.append(input.name, data[input.name] ?? "");
          }
        }
      }

      const response = await fetch("/api/order", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("The appraisal request was not accepted");
      }

      // Analytics failures must not hide a successfully delivered request.
      try {
        trackFormSubmit(formType, data);
      } catch {
        // The enquiry has already been received by the server.
      }

      reset();
      setIsSubmitted(true);
    } catch (error) {
      setErrorMessage("Sorry, we couldn't confirm your request.");
    }
  };

  return (
    <>
      {isSubmitted ? (
        <div role="status" className=" text-center mx-auto mt-16 max-w-4xl sm:mt-20 border-x-4 border-blue-500 px-10">
          <p>
            Thank you for your submission! <br />
            Contact us at (416) 450-2336 or info@alphaappraisals.ca.
          </p>
        </div>
      ) : (
        <form
          ref={formRef}
          noValidate
          onSubmit={handleSubmit(onSubmit, (errors) => {
            setErrorMessage("");
            const first = fields.find(field => errors[field.name]);
            if (first) requestAnimationFrame(() => focusField(first.name));
          })}
          autoComplete="on"
          className="mx-auto font-cinzel mt-16 max-w-4xl sm:mt-20 border-x-4 border-blue-900 px-5"
        >
          <p className="mb-4 text-sm">Fields marked <span className="text-red-700 dark:text-red-300">*</span> are required. Other details can be discussed with your appraiser.</p>
          {Object.keys(formState.errors).length > 0 && (
            <div role="alert" className="mb-6 rounded border border-red-500 bg-red-50 p-4 text-red-900 dark:bg-gray-900 dark:text-red-200">
              <p className="font-semibold">Please check the highlighted fields.</p>
              <ul className="mt-2 list-disc pl-5">
                {fields.filter(field => formState.errors[field.name]).map(field => (
                  <li key={field.name}><button type="button" className="underline" onClick={() => focusField(field.name)}>{field.label}</button></li>
                ))}
              </ul>
            </div>
          )}
          <style jsx>{`
            form :global([aria-invalid="true"]) { outline: 2px solid #dc2626; outline-offset: 2px; }
            form :global(input), form :global(select), form :global(textarea) { scroll-margin-top: 10rem; }
          `}</style>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2  gap-y-2">
            {inputs.map((box) => (
              <div
                key={box.box}
                className="border-2 p-6 border-gray-300  dark:border-slate-800 "
              >
                <h2 className="text-xl pb-5 border-gray-300 font-semibold dark:text-white ">
                  {box.box}
                </h2>
                {box.inputs.map((input) =>
  input.type === "select" ? (
    <div key={input.name}>
      <label htmlFor={input.name} className="block text-sm font-semibold leading-6">
        {fieldCaption(input)}
      </label>
      <select
        {...register(input.name)}
        className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
      >
        {input.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Show "Other" input field when "Other" is selected */}
      {watch(input.name) === "Other" && (
        <>
          <label htmlFor={input.name} className="block text-blue-700 dark:text-yellow-600 pt-2 text-sm font-semibold leading-6">
            If Other, Please Specify:
          </label>
          <input
            type="text"
            className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
            {...register(`${input.name} Other`)}
          />
        </>
      )}
    </div>


                  ) : input.name === "Intended use of this appraisal Power of Sale or Sherrifs Sale" ? (
                    <div key={input.name}>
                      <h3>
                        <label htmlFor={input.name} className="block pt-2 text-sm font-semibold leading-6 w-max col-span-1">
                          {fieldCaption(input)}
                        </label>{" "}
                        <div className="mt-2.5 col-span-1 ">
                          <select
                            {...register(input.name)}
                            className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                          >
                            <option value="Owner" defaultValue>
                              Power of Sale
                            </option>
                            <option value="Tenant">
                            Foreclosure 
                            </option>
                            <option value="Vacant">
                              Sheriff's Sale of The Property
                            </option>
                           
                          </select>{" "}
                        </div>{" "}
                        {watch(input.name) === "Other" && (
                          <>
                            <label htmlFor={input.name} className="block text-blue-700 dark:text-yellow-600 pt-2 text-sm font-semibold leading-6 w-max col-span-1">
                              If Other, Please Describe:
                            </label>{" "}
                            <input
                              type="text"
                              className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                              {...register(`${input.name} Other`)}
                            />
                          </>
                        )}
                      </h3>
                    </div>
                  ) : input.name === "Purpose of insolvency-propsal" ? (
                    <div key={input.name}>
                      <h3>
                        <label htmlFor={input.name} className="block pt-2 text-sm font-semibold leading-6 w-max col-span-1">
                          {fieldCaption(input)}
                        </label>{" "}
                        <div className="mt-2.5 col-span-1 ">
                          <select
                            {...register(input.name)}
                            className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                          >
                            <option value="Owner" defaultValue>
                              Proposal Proceeding
                            </option>
                            <option value="Tenant">
                              Insolvency Proceeding
                            </option>
                            <option value="Vacant">
                              Bankruptcy Asset Valuation
                            </option>
                            <option value="Builder">
                              Asset Valuation Consumer Proposal
                            </option>
                          </select>{" "}
                        </div>{" "}
                        {watch(input.name) === "Other" && (
                          <>
                            <label htmlFor={input.name} className="block text-blue-700 dark:text-yellow-600 pt-2 text-sm font-semibold leading-6 w-max col-span-1">
                              If Other, Please Describe:
                            </label>{" "}
                            <input
                              type="text"
                              className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                              {...register(`${input.name} Other`)}
                            />
                          </>
                        )}
                      </h3>
                    </div>
                  ) : input.name ===
                    "Requested By Mortgage Broker or Lender" ? (
                    <div key={input.name}>
                      <h3>
                        <label htmlFor={input.name} className="block pt-2 text-sm font-semibold leading-6 w-max col-span-1">
                          {fieldCaption(input)}
                        </label>{" "}
                        <div className="mt-2.5 col-span-1 ">
                          <select
                            {...register(input.name)}
                            className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                          >
                            <option value="Mortgage Broker" defaultValue>
                              Mortgage Broker
                            </option>
                            <option value="Lender">Lender</option>
                          </select>{" "}
                        </div>
                      </h3>
                    </div>
                  ) : input.name === "Current Or Retrospective" ? (
                    <div key={input.name}>
                      <h3>
                        <label htmlFor={input.name} className="block pt-2 text-sm font-semibold leading-6 w-max col-span-1">
                          {fieldCaption(input)}
                        </label>{" "}
                        <div className="mt-2.5 col-span-1 ">
                          <select
                            {...register(input.name)}
                            className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                          >
                            <option value="Current" defaultValue>
                              Current
                            </option>
                            <option value="Retrospective">Retrospective</option>
                          </select>{" "}
                        </div>
                      </h3>
                    </div>
                  ) : input.name === "Requested By Lawyer or Acountant" ? (
                    <div key={input.name}>
                      <h3>
                        <label htmlFor={input.name} className="block pt-2 text-sm font-semibold leading-6 w-max col-span-1">
                          {fieldCaption(input)}
                        </label>{" "}
                        <div className="mt-2.5 col-span-1 ">
                          <select
                            {...register(input.name)}
                            className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                          >
                            <option value="Lawyer" defaultValue>
                              Lawyer
                            </option>
                            <option value="Accountant">Accountant</option>
                            <option value="Other">Other</option>
                          </select>{" "}
                        </div>
                        {watch(input.name) === "Other" && (
                          <>
                            <label htmlFor={input.name} className="block text-blue-700 dark:text-yellow-600 pt-2 text-sm font-semibold leading-6 w-max col-span-1">
                              If Other, Please Describe:
                            </label>{" "}
                            <input
                              type="text"
                              className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                              {...register(`${input.name} Other`)}
                            />
                          </>
                        )}
                      </h3>
                    </div>
                  ) : input.name === "Intended Use of This Appraisal" ? (
                    <div key={input.name}>
                      <h3>
                        <label htmlFor={input.name} className="block pt-2 text-sm font-semibold leading-6 w-max col-span-1">
                          {fieldCaption(input)}
                        </label>{" "}
                        <div className="mt-2.5 col-span-1 ">
                          <select
                            {...register(input.name, { required: true })}
                            className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                          >
                            <option value="1st Mortgage" defaultValue>
                              First Mortgage
                            </option>
                            <option value="2nd Mortgage">2nd Mortgage</option>
                            <option value="Other">Other</option>
                          </select>{" "}
                        </div>{" "}
                      </h3>{" "}
                      {watch(input.name) === "Other" && (
                        <>
                          <label htmlFor={input.name} className="block text-blue-700 dark:text-yellow-600 pt-2 text-sm font-semibold leading-6 w-max col-span-1">
                            If Other, Please Describe:
                          </label>{" "}
                          <input
                            type="text"
                            className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                            {...register(`${input.name} Other`)}
                          />
                        </>
                      )}
                    </div>
                  ) : input.name ===
                    "Intended use of this appraisal lawyer/accountant/other" ? (
                    <div key={input.name}>
                      <h3>
                        <label htmlFor={input.name} className="block pt-2 text-sm font-semibold leading-6 w-max col-span-1">
                          {fieldCaption(input)}
                        </label>{" "}
                        <div className="mt-2.5 col-span-1 ">
                          <select
                            {...register(input.name, { required: true })}
                            className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                          >
                            <option
                              value="Matrimonial/Divorce-Separation"
                              defaultValue
                            >
                              Matrimonial/Divorce-Separation of Assets
                            </option>
                            <option value="Litigation">Litigation</option>
                            <option value="Capital Gains Tax Reporting">
                              Capital Gains Tax Reporting
                            </option>
                            <option value="Spousal cohabitation agreement appraisal">
                              Spousal cohabitation agreement appraisal
                            </option>
                            <option value="Premarital agreement appraisal">
                              Premarital agreement appraisal
                            </option>

                            <option value="Other">Other</option>
                          </select>{" "}
                        </div>{" "}
                        {watch(input.name) === "Other" && (
                          <>
                            <label htmlFor={input.name} className="block text-blue-700 dark:text-yellow-600 pt-2 text-sm font-semibold leading-6 w-max col-span-1">
                              If Other, Please Describe:
                            </label>{" "}
                            <input
                              type="text"
                              className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                              {...register(`${input.name} Other`)}
                            />
                          </>
                        )}
                      </h3>
                    </div>
                  ) : input.name === "Method of Payment" ? (
                    <div key={input.name}>
                      <h3>
                        <label htmlFor={input.name} className="block text-sm font-semibold leading-6 w-max col-span-1">
                          {fieldCaption(input)}
                        </label>{" "}
                        <div className="mt-2.5 col-span-1 ">
                          <select
                            {...register(input.name)}
                            className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                          >
                            <option value="etransfer" defaultValue>
                              E-Transfer
                            </option>
                            <option value="Cash">
                              Cash (Receipt Provided)
                            </option>
                            <option value="Certified Cheque">
                              Certified Cheque or Money Order
                            </option>
                          </select>{" "}
                        </div>
                      </h3>
                    </div>
                  ) : input.name === "Under Major Renovation" ? (
                    <div key={input.name}>
                      <h3>
                        <label htmlFor={input.name} className="block text-sm pt-2 font-semibold leading-6">
                          {fieldCaption(input)}
                        </label>{" "}
                        <div className="mt-2.5 col-span-1 ">
                          <select
                            {...register(input.name)}
                            className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                          >
                            <option value="No" defaultValue>
                              No
                            </option>
                            <option value="Yes">Yes</option>
                          </select>{" "}
                        </div>
                      </h3>
                    </div>
                  ) : input.name === "Intended Use Of Appraisal" ? (
                    <div key={input.name}>
                      <h3>
                        <label htmlFor={input.name} className="block text-sm pt-2 font-semibold leading-6">
                          {fieldCaption(input)}
                        </label>{" "}
                        <div className="mt-2.5 col-span-1 ">
                          <select
                            {...register(input.name, { required: true })}
                            required={true}
                            className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                          >
                            <option
                              value="Probate Estate Division"
                              defaultValue
                            >
                              Probate Estate Division
                            </option>
                            <option value="Capital Gains Tax Planning">
                              Capital Gains Tax Planning
                            </option>
                            <option value="Matrimonial/Divorce-Separation of Assets">
                              Matrimonial/Divorce-Separation of Assets
                            </option>
                            <option
                              value="To establish potential selling price / Pre-listing"
                              defaultValue
                            >
                              To establish potential selling price / Pre-listing
                              valuation
                            </option>

                            <option value="Spousal cohabitation agreement appraisal">
                              Spousal cohabitation agreement appraisal
                            </option>
                            <option value="Premarital agreement appraisal">
                              Premarital agreement appraisal
                            </option>

                            <option value="Other">Other</option>
                          </select>{" "}
                        </div>
                      </h3>{" "}
                      {watch(input.name) === "Other" && (
                        <>
                          <label htmlFor={input.name} className="block text-blue-700 dark:text-yellow-600 pt-2 text-sm font-semibold leading-6 w-max col-span-1">
                            If Other, Please Describe:
                          </label>{" "}
                          <input
                            type="text"
                            className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                            {...register(`${input.name} Other`)}
                          />
                        </>
                      )}
                    </div>
                  ) : input.display === "none" ? null : input.name ===
                    "Additional Information" ? (
                    <div key={input.name}>
                      <label htmlFor={input.name} className="block text-sm pt-2 font-semibold leading-6">
                        {fieldCaption(input)}
                      </label>
                      <div className="mt-2.5">
                        <textarea
                          {...register(input.name, { required: false })}
                          name={input.name}
                          id={input.name}
                      
                          className="block w-full rounded-md text-black border-0 px-3.5 py-2  shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 resize-none"
                          rows={3} // Set the initial number of rows for the textarea
                        />
                      </div>
                    </div>
                  ) : (
                    <div key={input.name}>
                      <label htmlFor={input.name} className="block text-sm pt-2 font-semibold leading-6">
                        {fieldCaption(input)}
                      </label>
                      <div className="mt-2.5">
                        <input
                          type={input.type}
                          {...register(input.name, {
                            required:
                              input.name ===
                                "Requested By Full Legal Name Property Owner" ||
                              input.name === "Phone"
                                ? true
                                : false,
                          })}
                          name={input.name}
                          id={input.name}
                      
                          className="block w-full rounded-md text-black border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>
                  )
                )}
              </div>
            ))}{" "}
          </div>{" "}
          <div className="mt-2">
            <h2 className="text-center  p-5 text-sm">
              After submitting, please e-mail us any attachments: (APS, Listing,
              Builder Agreement etc.)
            </h2>
          </div>
          {errorMessage && (
            <div role="alert" className="my-6 rounded-2xl border border-amber-200 bg-amber-50 p-6 font-sans shadow-sm sm:p-8 dark:border-amber-700/40 dark:bg-slate-900">
              <div className="flex items-start gap-4">
                <span aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-100 text-2xl font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">!</span>
                <div>
                  <h3 className="text-2xl font-semibold leading-snug tracking-tight text-slate-900 sm:text-3xl dark:text-white">{errorMessage}</h3>
                  <p className="mt-3 text-lg leading-relaxed text-slate-600 dark:text-slate-300">Please try again, or call us directly for help with your appraisal request.</p>
                  <a href="tel:+14164502336" className="mt-5 inline-flex min-h-[48px] items-center justify-center gap-3 rounded-lg bg-blue-700 px-5 py-3 text-lg font-semibold text-white transition hover:bg-blue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-500 dark:bg-blue-600 dark:hover:bg-blue-500">
                    <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 0 1 2-2h3l2 5-2 2a16 16 0 0 0 6 6l2-2 5 2v3a2 2 0 0 1-2 2C10 21 3 14 3 5Z" /></svg>
                    (416) 450-2336
                  </a>
                </div>
              </div>
            </div>
          )}
          <div className="mt-0 sm:mt-0 flex center py-2 justify-center items-center">
            <button
              disabled={formState.isSubmitting}
              type="submit"
              className={`${errorMessage ? "border-slate-300 bg-white text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-white dark:hover:bg-slate-800" : "border-blue-700 bg-blue-700 text-white hover:bg-blue-800 dark:border-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500"} min-h-[52px] w-full rounded-xl border px-6 py-4 text-center font-sans text-lg font-semibold transition disabled:cursor-wait disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-500`}
            >
              {formState.isSubmitting ? (
                "Sending..."
              ) : errorMessage ? (
                "Try again"
              ) : (
                "Submit Your Appraisal Request"
              )}
            </button>
          </div>{" "}
        </form>
      )}{" "}
    </>
  );
}
